import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
const API = "/api";

type EmpikOffer = {
  id: string;
  offer_id: number | null;
  shop_sku: string | null;
  product_sku: string | null;
  ean: string | null;
  title: string | null;
  brand: string | null;
  category: string | null;
  price: number | null;
  currency: string | null;
  quantity: number | null;
  active: boolean;
  leadtime_to_ship: number | null;
  logistic_class: string | null;
  logistic_class_label: string | null;
  image_url: string | null;
};

type EmpikProduct = {
  product_id: string;
  product_id_type: string | null;
  product_sku: string | null;
  product_title: string | null;
  category_label: string | null;
  matched_by: string;
};

type Stats = { total: number; active: number; inactive: number; logistic_classes: string[] };

const LIMIT = 50;
// ===== B11: miniaturka — lzejsza wersja zdjecia z CDN (s200 zamiast original) =====
const thumbUrl = (u: string | null | undefined) => (u || '').replace('/original/', '/s200/');


const s: { [k: string]: React.CSSProperties } = {
  input: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, background: '#fff' },
  btn: { border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer', background: '#fff' },
  th: { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: '#6b7280', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' },
  td: { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  label: { fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', flexDirection: 'column', gap: 4 },
};

export default function EmpikOffers() {
  const [offers, setOffers] = useState<EmpikOffer[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, active: 0, inactive: 0, logistic_classes: [] });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [activeF, setActiveF] = useState('');
  const [lcF, setLcF] = useState('');
  const [sort, setSort] = useState('offer_id');
  const [order, setOrder] = useState('desc');
  // ===== B12: sortowanie kolumnowe =====
  const setSortCol = (k: string) => {
    if (sort === k) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setSort(k); setOrder('asc'); }
    setPage(0);
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  // ===== B10: zaznaczanie ofert + edycja masowa =====
  const [sel, setSel] = useState<{ [key: string]: EmpikOffer }>({});
  const selArr: EmpikOffer[] = Object.values(sel);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ priceMode: 'set', priceVal: '', quantity: '', leadtime_to_ship: '', logistic_class: '' });
  const [bulkRun, setBulkRun] = useState({ running: false, done: 0, total: 0, current: '' });
  const [bulkLog, setBulkLog] = useState<string[]>([]);
  const [activating, setActivating] = useState(false); // B15
  const bulkCancel = useRef(false);


  const [edit, setEdit] = useState<EmpikOffer | null>(null);
  const [editForm, setEditForm] = useState({ price: '', quantity: '', leadtime_to_ship: '', logistic_class: '' });
  const [saving, setSaving] = useState(false);

  const [creating, setCreating] = useState(false);
  const [cQuery, setCQuery] = useState('');
  const [cProducts, setCProducts] = useState<EmpikProduct[] | null>(null);
  const [cProduct, setCProduct] = useState<EmpikProduct | null>(null);
  const [cSearching, setCSearching] = useState(false);
  const [cForm, setCForm] = useState({ shop_sku: '', price: '', quantity: '', leadtime_to_ship: '1', logistic_class: '' });
  const [cSaving, setCSaving] = useState(false);
  const [gpsrInfo, setGpsrInfo] = useState(false);

  const load = useCallback(async (refresh?: boolean) => {
    setLoading(true);
    setErr('');
    try {
      const r = await axios.get(API + '/empik-offers', {
        params: {
          q: (q || '').trim(),  // B14: ignoruj spacje na poczatku/koncu frazy
          active: activeF,
          logistic_class: lcF,
          sort,
          order,
          page: page + 1,
          page_size: LIMIT,
          ...(refresh ? { refresh: 1 } : {}),
        },
      });
      setOffers((r.data?.offers || []) as EmpikOffer[]);
      setTotal(r.data?.total || 0);
      if (r.data?.stats) setStats(r.data.stats as Stats);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Nie udało się pobrać ofert Empik');
    } finally {
      setLoading(false);
    }
  }, [q, activeF, lcF, sort, order, page]);

  // ===== B15: aktywacja ofert nieaktywnych — POST /api/empik-offers/activate =====
  // Backend buduje wiersz OF22 z bieżących danych oferty i czyści okres dostępności
  // (available_started/available_ended = null — metoda zweryfikowana T1c/T3).
  const runActivate = async (list: EmpikOffer[]) => {
    const targets = list.filter(o => o && !o.active && o.offer_id != null);
    if (!targets.length) { setErr('Brak nieaktywnych ofert do aktywacji w tej selekcji'); return; }
    if (!window.confirm('Aktywować ' + targets.length + (targets.length === 1 ? ' ofertę' : ' ofert') + ' w Empik? (wyczyszczenie okresu dostępności)')) return;
    setActivating(true);
    setErr('');
    setMsg('');
    try {
      const r = await axios.post(API + '/empik-offers/activate', { offer_ids: targets.map(o => String(o.offer_id)) });
      const d = r.data || {};
      const act = (d.activated || []).length;
      const skip = (d.skipped || []);
      if (d.ok === true) {
        let m = '▶️ ' + (d.detail || ('aktywowano ' + act + ' ofert'));
        if (skip.length) m += ' · pominięto ' + skip.length + ': ' + skip.slice(0, 6).map((s: any) => s.offer_id + ' (' + s.reason + ')').join('; ') + (skip.length > 6 ? ' …' : '');
        setMsg(m);
        load();
      } else if (d.ok === false) {
        setErr('❌ ' + (d.detail || 'Empik odrzucił zmianę') + (skip.length ? ' · pominięto: ' + skip.length : ''));
      } else {
        setMsg('⏳ ' + (d.detail || 'import w toku — odśwież listę za chwilę'));
        window.setTimeout(() => { load(); }, 5000);
      }
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Nie udało się aktywować ofert (POST /api/empik-offers/activate)');
    } finally {
      setActivating(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q, activeF, lcF, sort, order]);

  const openEdit = (o: EmpikOffer) => {
    setEdit(o);
    setEditForm({
      price: o.price != null ? String(o.price) : '',
      quantity: o.quantity != null ? String(o.quantity) : '',
      leadtime_to_ship: o.leadtime_to_ship != null ? String(o.leadtime_to_ship) : '',
      logistic_class: o.logistic_class || '',
    });
  };

  // ===== B10: zaznaczanie / odznaczanie =====
  const selectable = offers.filter(o => o.offer_id != null);
  const allPageSelected = selectable.length > 0 && selectable.every(o => !!sel[String(o.offer_id)]);
  const ofert = (n: number) => n === 1 ? 'oferta' : ([2, 3, 4].indexOf(n % 10) >= 0 && [12, 13, 14].indexOf(n % 100) < 0 ? 'oferty' : 'ofert');

  const toggleSel = (o: EmpikOffer) => {
    setSel(s => {
      const k = String(o.offer_id);
      const n = { ...s };
      if (n[k]) delete n[k]; else n[k] = o;
      return n;
    });
  };

  const toggleAllPage = () => {
    setSel(s => {
      const n = { ...s };
      if (selectable.length > 0 && selectable.every(o => n[String(o.offer_id)])) {
        selectable.forEach(o => { delete n[String(o.offer_id)]; });
      } else {
        selectable.forEach(o => { n[String(o.offer_id)] = o; });
      }
      return n;
    });
  };


  // ===== B10: edycja masowa - kolejne PATCH-e istniejacym endpointem (odstepy + retry 429 po stronie backendu z B9) =====
  const runBulk = async (presetQty0: boolean) => {
    const list = Object.values(sel);
    if (!list.length) { setErr('Zaznacz przynajmniej jedną ofertę'); return; }
    if (!presetQty0) {
      if (bulkForm.priceVal !== '') {
        const pv = parseFloat(bulkForm.priceVal.replace(',', '.'));
        if (isNaN(pv)) { setErr('Podaj poprawną wartość ceny'); return; }
        if (bulkForm.priceMode === 'set' && pv <= 0) { setErr('Nowa cena musi być większa od 0'); return; }
      }
      if (bulkForm.quantity !== '') {
        const qv = parseInt(bulkForm.quantity, 10);
        if (isNaN(qv) || qv < 0) { setErr('Podaj poprawną ilość'); return; }
      }
      if (bulkForm.leadtime_to_ship !== '') {
        const lv = parseInt(bulkForm.leadtime_to_ship, 10);
        if (isNaN(lv) || lv < 1 || lv > 30) { setErr('Czas wysyłki: podaj liczbę dni 1-30'); return; }
      }
      if (bulkForm.priceVal === '' && bulkForm.quantity === '' && bulkForm.leadtime_to_ship === '' && !bulkForm.logistic_class) {
        setErr('Podaj co najmniej jedną zmianę (cena / ilość / czas wysyłki / klasa)');
        return;
      }
    }
    bulkCancel.current = false;
    setBulkOpen(false);
    setBulkLog([]);
    setErr('');
    setMsg('');
    setBulkRun({ running: true, done: 0, total: list.length, current: '' });
    const okIds: string[] = [];
    const pendIds: string[] = [];
    const failIds: string[] = [];
    const skipN: string[] = [];
    const errN: string[] = [];
    for (let i = 0; i < list.length; i++) {
      if (bulkCancel.current) break;
      const o = list[i];
      const oid = o.offer_id;
      const label = o.shop_sku || (oid != null ? String(oid) : '(bez nr)');
      setBulkRun(r => ({ ...r, done: i, current: (i + 1) + '/' + list.length + ' · ' + (o.title || label) }));
      if (oid == null) { skipN.push(label + ': pominięto (brak nr oferty)'); failIds.push(String(o.offer_id)); continue; }
      const body: any = {};
      if (presetQty0) {
        body.quantity = 0;
      } else {
        if (bulkForm.priceVal !== '') {
          const pv = parseFloat(bulkForm.priceVal.replace(',', '.'));
          let np: number | null = null;
          if (bulkForm.priceMode === 'set') np = pv;
          else if (o.price != null) np = Math.round((bulkForm.priceMode === 'delta' ? o.price + pv : o.price * (1 + pv / 100)) * 100) / 100;
          if (np == null) { skipN.push(label + ': pominięto (brak ceny bazowej do przeliczenia)'); failIds.push(String(oid)); continue; }
          if (np <= 0) { skipN.push(label + ': pominięto (cena wynikowa <= 0)'); failIds.push(String(oid)); continue; }
          body.price = np;
        }
        if (bulkForm.quantity !== '') body.quantity = parseInt(bulkForm.quantity, 10);
        if (bulkForm.leadtime_to_ship !== '') body.leadtime_to_ship = parseInt(bulkForm.leadtime_to_ship, 10);
        if (bulkForm.logistic_class) body.logistic_class = bulkForm.logistic_class;
      }
      try {
        const r = await axios.patch(API + '/empik-offers/' + oid, body);
        if (r.data?.ok === false) { errN.push(label + ': ' + (r.data?.detail || 'Empik odrzucił zmianę')); failIds.push(String(oid)); }
        else if (r.data?.ok === null) pendIds.push(label);
        else okIds.push(String(oid));
      } catch (e: any) {
        errN.push(label + ': ' + (e?.response?.data?.detail || 'błąd zapisu'));
        failIds.push(String(oid));
      }
      await new Promise<void>(res => setTimeout(res, 400));
    }
    const przerwano = bulkCancel.current;
    const processed = okIds.length + pendIds.length + skipN.length + errN.length;
    setBulkRun(r => ({ ...r, running: false, done: list.length, current: '' }));
    const log: string[] = [];
    log.push('✔ Zapisano pomyślnie: ' + okIds.length + ' / ' + list.length);
    if (pendIds.length) log.push('⏳ Przyjęte do importu (wyniki po odświeżeniu): ' + pendIds.length);
    if (skipN.length) log.push('⏭ Pominięte: ' + skipN.length);
    if (errN.length) log.push('✖ Błędy: ' + errN.length);
    if (przerwano) log.push('⏹ Przerwano po ' + processed + ' z ' + list.length);
    if (skipN.length) log.push('— pominięte —');
    skipN.forEach(x => log.push(x));
    if (errN.length) log.push('— błędy —');
    errN.forEach(x => log.push(x));
    if (failIds.length || przerwano) log.push('ℹ Nieudane i niewysłane pozostają zaznaczone — popraw i ponów.');
    setBulkLog(log);
    const keepSel: { [key: string]: EmpikOffer } = {};
    failIds.forEach(k => { if (sel[k]) keepSel[k] = sel[k]; });
    if (przerwano) {
      for (let j = processed; j < list.length; j++) {
        const oo = list[j];
        keepSel[String(oo.offer_id)] = oo;
      }
    }
    setSel(keepSel);
    load();  // B14: dane sa juz w cache (overlay) — bez wymuszonego poboru z Empik
  };


  const saveEdit = async () => {
    if (!edit) return;
    const body: any = {};
    const price = parseFloat(editForm.price.replace(',', '.'));
    const qty = parseInt(editForm.quantity, 10);
    const lt = parseInt(editForm.leadtime_to_ship, 10);
    if (editForm.price !== '' && !isNaN(price)) body.price = price;
    if (editForm.quantity !== '' && !isNaN(qty)) body.quantity = qty;
    if (editForm.leadtime_to_ship !== '' && !isNaN(lt)) body.leadtime_to_ship = lt;
    if (editForm.logistic_class) body.logistic_class = editForm.logistic_class;
    if (!Object.keys(body).length) { setErr('Nic do zapisania'); return; }
    setSaving(true);
    setErr('');
    setMsg('');
    try {
      const r = await axios.patch(API + '/empik-offers/' + edit.offer_id, body);
      if (r.data?.ok === false) {
        setErr(r.data?.detail || 'Empik odrzucił zmianę');
      } else if (r.data?.ok === null) {
        setMsg(r.data?.detail || 'Import w toku — odśwież listę za chwilę');
        setEdit(null);
      } else {
        setMsg('Zapisano ✓' + (body.quantity === 0 ? ' (ilość 0 = oferta nieaktywna)' : ''));
        setEdit(null);
        load();  // B14: dane sa juz w cache (overlay) — bez wymuszonego poboru z Empik
      }
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Nie udało się zapisać zmian');
    } finally {
      setSaving(false);
    }
  };

  const searchProducts = async () => {
    if (cQuery.trim().length < 3) { setErr('Wpisz co najmniej 3 znaki (EAN lub SKU)'); return; }
    setCSearching(true);
    setErr('');
    setMsg('');
    setCProducts(null);
    try {
      const r = await axios.post(API + '/empik-offers/search-products', { query: cQuery.trim() });
      setCProducts((r.data?.products || []) as EmpikProduct[]);
      if (!(r.data?.products || []).length) setMsg('Brak produktów w katalogu Empik dla podanego EAN/SKU');
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Wyszukiwanie nieudane');
    } finally {
      setCSearching(false);
    }
  };

  const pickProduct = (p: EmpikProduct) => {
    setCProduct(p);
    setCForm(f => ({ ...f, shop_sku: f.shop_sku || p.product_sku || '' }));
    axios.get(API + '/empik-offers/gpsr-template')
      .then(r => setGpsrInfo((r.data?.fields || []).length > 0))
      .catch(() => setGpsrInfo(false));
  };

  const createOffer = async () => {
    const price = parseFloat(cForm.price.replace(',', '.'));
    const qty = parseInt(cForm.quantity, 10);
    const lt = parseInt(cForm.leadtime_to_ship || '1', 10);
    if (!cProduct) { setErr('Najpierw wyszukaj i wybierz produkt z katalogu'); return; }
    if (!cForm.shop_sku.trim()) { setErr('Podaj SKU oferty (shop_sku)'); return; }
    if (isNaN(price) || price <= 0) { setErr('Podaj poprawną cenę'); return; }
    if (isNaN(qty) || qty < 0) { setErr('Podaj poprawną ilość'); return; }
    setCSaving(true);
    setErr('');
    setMsg('');
    try {
      const r = await axios.post(API + '/empik-offers', {
        product_id: cProduct.product_id,
        product_id_type: cProduct.product_id_type || '',
        shop_sku: cForm.shop_sku.trim(),
        price,
        quantity: qty,
        leadtime_to_ship: isNaN(lt) ? 1 : lt,
        logistic_class: cForm.logistic_class || '',
      });
      if (r.data?.ok === false) {
        setErr(r.data?.detail || 'Empik odrzucił ofertę');
      } else if (r.data?.ok === null) {
        setMsg(r.data?.detail || 'Import w toku — odśwież listę za chwilę');
      } else {
        setMsg('Oferta utworzona ✓ — odświeżam listę');
        setCreating(false);
        setCProduct(null);
        setCProducts(null);
        setCQuery('');
        setCForm({ shop_sku: '', price: '', quantity: '', leadtime_to_ship: '1', logistic_class: '' });
        load(true);
      }
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Nie udało się utworzyć oferty');
    } finally {
      setCSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>🛒 Oferty Empik</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {stats.total} ofert · {stats.active} aktywnych · {stats.inactive} nieaktywnych{loading ? ' · ładowanie…' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={s.btnGhost} onClick={() => load(true)} disabled={loading}>🔄 Odśwież</button>
          <button style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }} onClick={() => { setCreating(true); setCProducts(null); setCProduct(null); setErr(''); setMsg(''); }}>➕ Nowa oferta</button>
        </div>
      </div>

      {(err || msg) && (
        <div style={{ padding: '10px 12px', borderRadius: 8, fontSize: 13, ...(err ? { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' } : { background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }) }}>
          {err || msg}
        </div>
      )}

      {selArr.length > 0 && !bulkRun.running && (
        <div data-b10="bulkbar" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <b style={{ fontSize: 13 }}>☑ Zaznaczono ofert: {selArr.length}</b>
          <button style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }} onClick={() => { setBulkForm({ priceMode: 'set', priceVal: '', quantity: '', leadtime_to_ship: '', logistic_class: '' }); setBulkLog([]); setBulkOpen(true); }}>✏️ Edytuj masowo</button>
          <button style={{ ...s.btn, background: '#b45309', color: '#fff' }} onClick={() => { if (window.confirm('Ustawić ilość 0 (oferty staną się nieaktywne) dla ' + selArr.length + ' zaznaczonych ofert?')) runBulk(true); }}>⏸ Dezaktywuj (ilość 0)</button>
          <button style={{ ...s.btn, background: '#059669', color: '#fff' }} disabled={activating} onClick={() => runActivate(Object.values(sel))}>{activating ? '⏳ Aktywuję…' : '▶️ Aktywuj zaznaczone'}</button>
          <button style={s.btnGhost} onClick={() => setSel({})}>✕ Wyczyść zaznaczenie</button>
        </div>
      )}

      {bulkLog.length > 0 && !bulkRun.running && (
        <div data-b10="bulklog" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <b>Wynik edycji masowej</b>
            <button style={{ ...s.btnGhost, padding: '4px 10px' }} onClick={() => setBulkLog([])}>✕ Zamknij</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0 24px' }}>
            {bulkLog.map((l, i) => (
              <div key={i} style={{ padding: '2px 0', color: l.indexOf('✖') === 0 ? '#b91c1c' : l.indexOf('✔') === 0 ? '#166534' : l.indexOf('⏭') === 0 ? '#92400e' : '#374151' }}>{l}</div>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setQ(searchInput); setPage(0); } }}
          placeholder="Szukaj: nazwa, SKU oferty, EAN, nr oferty…" style={{ ...s.input, flex: '1 1 240px' }} />
        <button style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }} onClick={() => { setQ(searchInput); setPage(0); }}>Szukaj</button>
        <select value={activeF} onChange={e => { setActiveF(e.target.value); setPage(0); }} style={s.input}>
          <option value="">Wszystkie statusy</option>
          <option value="true">Tylko aktywne</option>
          <option value="false">Tylko nieaktywne</option>
        </select>
        <select value={lcF} onChange={e => { setLcF(e.target.value); setPage(0); }} style={s.input}>
          <option value="">Klasa logist.: wszystkie</option>
          {(stats.logistic_classes || []).map(c => <option key={c} value={c}>Klasa {c}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: 34 }}>
                <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} title="Zaznacz / odznacz wszystkie widoczne na stronie" />
              </th>
              <th data-b12="th" style={{ ...s.th, cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }} onClick={() => setSortCol('shop_sku')} title="Kliknij, aby sortować">SKU oferty{sort === 'shop_sku' ? (order === 'asc' ? ' ▲' : ' ▼') : ''}</th>
              <th data-b12="th" style={{ ...s.th, cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }} onClick={() => setSortCol('title')} title="Kliknij, aby sortować">Nazwa / EAN{sort === 'title' ? (order === 'asc' ? ' ▲' : ' ▼') : ''}</th>
              <th style={s.th}>Kategoria</th>
              <th data-b12="th" style={{ ...s.th, cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }} onClick={() => setSortCol('price')} title="Kliknij, aby sortować">Cena{sort === 'price' ? (order === 'asc' ? ' ▲' : ' ▼') : ''}</th>
              <th data-b12="th" style={{ ...s.th, cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }} onClick={() => setSortCol('quantity')} title="Kliknij, aby sortować">Ilość{sort === 'quantity' ? (order === 'asc' ? ' ▲' : ' ▼') : ''}</th>
              <th style={s.th}>Wysyłka</th>
              <th style={s.th}>Klasa</th>
              <th style={s.th}>Status</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {offers.map(o => (
              <tr key={o.id} style={{ ...(sel[String(o.offer_id)] ? { background: '#eff6ff' } : {}) }}>
                <td style={s.td}>
                  <input type="checkbox" disabled={o.offer_id == null} checked={!!sel[String(o.offer_id)]} onChange={() => toggleSel(o)} />
                </td>
                <td style={{ ...s.td, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>{o.shop_sku || '—'}</td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div data-b11="thumb" style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {o.image_url ? (
                        <img src={thumbUrl(o.image_url)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { const t = e.target as HTMLImageElement; if (t && t.style) t.style.visibility = 'hidden'; }} />
                      ) : (
                        <span style={{ fontSize: 10, color: '#9ca3af' }}>brak zdjęcia</span>
                      )}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, maxWidth: 340 }}>{o.title || '—'}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{o.ean ? 'EAN: ' + o.ean : ''}{o.brand ? ' · ' + o.brand : ''}</div>
                    </div>
                  </div>
                </td>
                <td style={{ ...s.td, fontSize: 12, color: '#374151' }}>{o.category || '—'}</td>
                <td style={{ ...s.td, whiteSpace: 'nowrap', fontWeight: 600 }}>{o.price != null ? o.price.toFixed(2) + ' ' + (o.currency || 'PLN') : '—'}</td>
                <td style={{ ...s.td, whiteSpace: 'nowrap', color: (o.quantity || 0) <= 0 ? '#b91c1c' : '#111827' }}>{o.quantity != null ? o.quantity : '—'}</td>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{o.leadtime_to_ship ? o.leadtime_to_ship + ' d' : '—'}</td>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{o.logistic_class || '—'}</td>
                <td style={s.td}>
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, ...(o.active ? { background: '#dcfce7', color: '#166534' } : { background: '#f3f4f6', color: '#6b7280' }) }}>
                    {o.active ? 'AKTYWNA' : 'NIEAKTYWNA'}
                  </span>
                </td>
                <td style={s.td}>
                  {!o.active && o.offer_id != null && (
                    <button style={{ ...s.btn, padding: '4px 10px', background: '#059669', color: '#fff' }} disabled={activating} onClick={() => runActivate([o])}>▶️ Aktywuj</button>
                  )}{' '}
                  <button style={{ ...s.btnGhost, padding: '4px 10px' }} onClick={() => openEdit(o)}>✏️ Edytuj</button>
                </td>
              </tr>
            ))}
            {!offers.length && !loading && (
              <tr><td colSpan={10} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>Brak ofert{q ? ' dla podanego wyszukiwania' : ''}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
        <button style={s.btnGhost} disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Poprzednia</button>
        <span style={{ fontSize: 13, color: '#374151' }}>Strona {page + 1} z {pages}</span>
        <button style={s.btnGhost} disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)}>Następna →</button>
      </div>

      {bulkOpen && !bulkRun.running && (
        <div data-b10="bulkmodal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }} onClick={() => setBulkOpen(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>✏️ Edycja masowa — {selArr.length} {ofert(selArr.length)}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>Zmiany pójdą kolejno do Empik (ok. 0,5 s na ofertę, z odstępami — bezpiecznie dla limitów). Puste pola = bez zmian.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
              <label style={s.label}>Cena — tryb
                <select value={bulkForm.priceMode} onChange={e => setBulkForm(f => ({ ...f, priceMode: e.target.value }))} style={s.input}>
                  <option value="set">Ustaw nową cenę</option>
                  <option value="delta">Zmień o kwotę (+/-)</option>
                  <option value="percent">Zmień o procent (+/-)</option>
                </select>
              </label>
              <label style={s.label}>{bulkForm.priceMode === 'set' ? 'Nowa cena (PLN)' : bulkForm.priceMode === 'delta' ? 'Kwota zmiany (PLN, np. -5 lub +2.50)' : 'Zmiana % (np. -10 = taniej o 10%)'}
                <input type="number" step="any" value={bulkForm.priceVal} onChange={e => setBulkForm(f => ({ ...f, priceVal: e.target.value }))} placeholder="puste = bez zmian" style={s.input} />
              </label>
              <label style={s.label}>Ilość (0 = nieaktywna)
                <input type="number" min="0" value={bulkForm.quantity} onChange={e => setBulkForm(f => ({ ...f, quantity: e.target.value }))} placeholder="puste = bez zmian" style={s.input} />
              </label>
              <label style={s.label}>Czas wysyłki (dni 1-30)
                <input type="number" min="1" max="30" value={bulkForm.leadtime_to_ship} onChange={e => setBulkForm(f => ({ ...f, leadtime_to_ship: e.target.value }))} placeholder="puste = bez zmian" style={s.input} />
              </label>
              <label style={s.label}>Klasa logistyczna
                <select value={bulkForm.logistic_class} onChange={e => setBulkForm(f => ({ ...f, logistic_class: e.target.value }))} style={s.input}>
                  <option value="">— bez zmiany —</option>
                  {(stats.logistic_classes || []).map(c => <option key={c} value={c}>Klasa {c}</option>)}
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={s.btnGhost} onClick={() => setBulkOpen(false)}>Anuluj</button>
              <button style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }} onClick={() => runBulk(false)}>Zastosuj do {selArr.length} {selArr.length === 1 ? 'oferty' : 'ofert'}</button>
            </div>
          </div>
        </div>
      )}

      {bulkRun.running && (
        <div data-b10="bulkprogress" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 12 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 560 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>⏳ Wysyłanie zmian do Empik… {bulkRun.done}/{bulkRun.total}</div>
            <div style={{ height: 8, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden', marginBottom: 8 }}>
              <div style={{ height: '100%', width: (bulkRun.total ? Math.round((bulkRun.done / bulkRun.total) * 100) : 0) + '%', background: '#1d4ed8', transition: 'width .2s' }} />
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bulkRun.current}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button style={{ ...s.btn, background: '#b91c1c', color: '#fff' }} onClick={() => { bulkCancel.current = true; }}>⏹ Przerwij (po bieżącej ofercie)</button>
            </div>
          </div>
        </div>
      )}


      {edit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }} onClick={() => setEdit(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Edycja oferty Empik #{edit.offer_id}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>{edit.title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <label style={s.label}>Cena ({edit.currency || 'PLN'})
                <input type="number" step="0.01" min="0" value={editForm.price} onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))} style={s.input} />
              </label>
              <label style={s.label}>Ilość (0 = nieaktywna)
                <input type="number" min="0" value={editForm.quantity} onChange={e => setEditForm(f => ({ ...f, quantity: e.target.value }))} style={s.input} />
              </label>
              <label style={s.label}>Czas wysyłki (dni 1-30)
                <input type="number" min="1" max="30" value={editForm.leadtime_to_ship} onChange={e => setEditForm(f => ({ ...f, leadtime_to_ship: e.target.value }))} style={s.input} />
              </label>
              <label style={s.label}>Klasa logistyczna
                <select value={editForm.logistic_class} onChange={e => setEditForm(f => ({ ...f, logistic_class: e.target.value }))} style={s.input}>
                  <option value="">— bez zmiany —</option>
                  {(stats.logistic_classes || []).map(c => <option key={c} value={c}>Klasa {c}</option>)}
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={s.btnGhost} onClick={() => setEdit(null)}>Anuluj</button>
              <button style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }} disabled={saving} onClick={saveEdit}>{saving ? 'Zapisuję…' : 'Zapisz zmiany'}</button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 24, overflowY: 'auto' }} onClick={() => setCreating(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>➕ Nowa oferta Empik</div>

            {!cProduct && (
              <>
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>
                  Krok 1 — znajdź produkt w katalogu Empik po <b>EAN</b> (lub SKU). Oferta zawsze dotyczy istniejącego produktu katalogowego.
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <input value={cQuery} onChange={e => setCQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') searchProducts(); }}
                    placeholder="np. 5904865400171" style={{ ...s.input, flex: 1 }} autoFocus />
                  <button style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }} disabled={cSearching} onClick={searchProducts}>{cSearching ? 'Szukam…' : 'Szukaj produktu'}</button>
                </div>
                {cProducts && cProducts.length > 0 && (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                    {cProducts.map(p => (
                      <div key={p.product_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '10px 12px', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{p.product_title || '(bez nazwy)'}</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>ID: {p.product_id}{p.category_label ? ' · ' + p.category_label : ''}</div>
                        </div>
                        <button style={{ ...s.btn, background: '#059669', color: '#fff' }} onClick={() => pickProduct(p)}>Wybierz</button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {cProduct && (
              <>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 13 }}>
                  Wybrany produkt: <b>{cProduct.product_title}</b> (ID: {cProduct.product_id})
                  <button style={{ ...s.btnGhost, marginLeft: 10, padding: '2px 8px', fontSize: 11 }} onClick={() => { setCProduct(null); setCProducts(null); }}>zmień</button>
                </div>
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>Krok 2 — dane oferty</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                  <label style={s.label}>SKU oferty (unikalne w sklepie)
                    <input value={cForm.shop_sku} onChange={e => setCForm(f => ({ ...f, shop_sku: e.target.value }))} style={s.input} />
                  </label>
                  <label style={s.label}>Cena (PLN)
                    <input type="number" step="0.01" min="0" value={cForm.price} onChange={e => setCForm(f => ({ ...f, price: e.target.value }))} style={s.input} />
                  </label>
                  <label style={s.label}>Ilość
                    <input type="number" min="0" value={cForm.quantity} onChange={e => setCForm(f => ({ ...f, quantity: e.target.value }))} style={s.input} />
                  </label>
                  <label style={s.label}>Czas wysyłki (dni 1-30)
                    <input type="number" min="1" max="30" value={cForm.leadtime_to_ship} onChange={e => setCForm(f => ({ ...f, leadtime_to_ship: e.target.value }))} style={s.input} />
                  </label>
                  <label style={s.label}>Klasa logistyczna
                    <select value={cForm.logistic_class} onChange={e => setCForm(f => ({ ...f, logistic_class: e.target.value }))} style={s.input}>
                      <option value="">— domyślna —</option>
                      {(stats.logistic_classes || []).map(c => <option key={c} value={c}>Klasa {c}</option>)}
                    </select>
                  </label>
                </div>
                {gpsrInfo && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>Dane GPSR (adres i kontakt podmiotu odpowiedzialnego) zostaną wypełnione automatycznie na podstawie istniejących ofert.</div>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button style={s.btnGhost} onClick={() => setCreating(false)}>Anuluj</button>
                  <button style={{ ...s.btn, background: '#059669', color: '#fff' }} disabled={cSaving} onClick={createOffer}>{cSaving ? 'Tworzę…' : 'Utwórz ofertę'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
